import { Modal, Button, Label, Textarea, Select } from "flowbite-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Ticket, KbIssue, KbSolution } from "@/sharedTypes";
import { HiArrowRight } from "react-icons/hi";
import { useGetKbIssueByIdQuery } from "@/api/kbApi/kbIssue";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    ai_issue_text?: string;
    ai_solution_text?: string;
    kb_issue_id?: number | null;
  }) => void;
  ticket: Ticket | null;
  kbIssues: KbIssue[];
  vectorLanguage?: "en" | "tr";
}

function appendText(current: string, text: string): string {
  if (!current.trim()) return text;
  return current + "\n" + text;
}

export default function TicketAiEditModal({
  open,
  onClose,
  onSubmit,
  ticket,
  kbIssues,
  vectorLanguage,
}: Props) {
  const { t } = useTranslation(["knowledgeBase", "common"]);
  const [aiIssueText, setAiIssueText] = useState("");
  const [aiSolutionText, setAiSolutionText] = useState("");
  const [kbIssueId, setKbIssueId] = useState<number | null>(null);
  const [appendedSources, setAppendedSources] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (ticket && open) {
      setAiIssueText(ticket.ai_issue_text || ticket.ticket_description || "");
      setAiSolutionText(ticket.ai_solution_text || "");
      setKbIssueId(ticket.kb_issue_id || null);
      setAppendedSources(new Set());
    }
  }, [ticket, open]);

  const appendOnce = (
    key: string,
    setter: React.Dispatch<React.SetStateAction<string>>,
    text: string
  ) => {
    if (appendedSources.has(key)) return;
    setter((prev) => appendText(prev, text));
    setAppendedSources((prev) => new Set(prev).add(key));
  };

  const { data: selectedKbIssueData } = useGetKbIssueByIdQuery(kbIssueId!, {
    skip: !kbIssueId,
  });
  const linkedSolutions: KbSolution[] = selectedKbIssueData?.solutions || [];

  const handleSubmit = () => {
    onSubmit({
      ai_issue_text: aiIssueText,
      ai_solution_text: aiSolutionText,
      kb_issue_id: kbIssueId,
    });
  };

  if (!ticket) return null;

  return (
    <Modal show={open} onClose={onClose} size="5xl">
      <Modal.Header>{t("knowledgeBase:editTicketAi")}</Modal.Header>
      <Modal.Body className="max-h-[75vh] overflow-y-auto">
        {vectorLanguage && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300">
            <span className="font-medium">{t("knowledgeBase:milvusLanguageLabel")}:</span>
            <span className="rounded bg-blue-100 px-1.5 py-0.5 font-mono text-xs font-semibold uppercase dark:bg-blue-900">
              {vectorLanguage === "en" ? "EN — English" : "TR — Türkçe"}
            </span>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ===== LEFT: Source Data ===== */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
              {t("knowledgeBase:sourceData")}
            </h3>

            {/* Ticket Title → Issue */}
            <SourceBlock
              label={t("knowledgeBase:ticketTitle")}
              text={ticket.ticket_title}
              actionLabel={appendedSources.has("title_issue") ? t("knowledgeBase:appended") : t("knowledgeBase:appendToIssue")}
              disabled={appendedSources.has("title_issue")}
              onAction={() => appendOnce("title_issue", setAiIssueText, ticket.ticket_title)}
            />

            {/* Ticket Description → Issue */}
            <SourceBlock
              label={t("knowledgeBase:ticketDescription")}
              text={ticket.ticket_description}
              actionLabel={appendedSources.has("desc_issue") ? t("knowledgeBase:appended") : t("knowledgeBase:appendToIssue")}
              disabled={appendedSources.has("desc_issue")}
              onAction={() => appendOnce("desc_issue", setAiIssueText, ticket.ticket_description)}
              clamp
            />

            {/* Resolution Note → Solution */}
            <div className="rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10 p-3">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-500 dark:text-blue-400">
                {t("knowledgeBase:resolutionNote")}
              </span>
              {ticket.resolution_note ? (
                <>
                  <p className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap line-clamp-6">
                    {ticket.resolution_note}
                  </p>
                  <div className="mt-2">
                    <AppendButton
                      label={appendedSources.has("resolution_solution") ? t("knowledgeBase:appended") : t("knowledgeBase:appendToSolution")}
                      disabled={appendedSources.has("resolution_solution")}
                      onClick={() => appendOnce("resolution_solution", setAiSolutionText, ticket.resolution_note || "")}
                    />
                  </div>
                </>
              ) : (
                <p className="mt-1 text-xs text-gray-400 italic">
                  {t("knowledgeBase:noResolutionNote")}
                </p>
              )}
            </div>

            {/* KB Linking */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                {t("knowledgeBase:kbLinking")}
              </h3>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="kb_issue_id" value={t("knowledgeBase:linkKbIssue")} />
                  <Select
                    id="kb_issue_id"
                    sizing="sm"
                    value={kbIssueId || ""}
                    onChange={(e) => {
                      setKbIssueId(e.target.value ? Number(e.target.value) : null);
                    }}
                  >
                    <option value="">{t("knowledgeBase:noneSelected")}</option>
                    {kbIssues.map((issue) => (
                      <option key={issue.id} value={issue.id}>
                        {issue.kb_issue_text?.substring(0, 80)}
                        {(issue.kb_issue_text?.length || 0) > 80 ? "..." : ""}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Linked solutions for selected issue — paste to solution text */}
                {kbIssueId && linkedSolutions.length > 0 && (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                      {t("knowledgeBase:linkedSolutions")}
                    </span>
                    {linkedSolutions.map((sol: KbSolution) => {
                      const key = `kb_sol_${sol.id}`;
                      const used = appendedSources.has(key);
                      return (
                        <div
                          key={sol.id}
                          className="flex items-start gap-2 rounded border border-gray-200 dark:border-gray-600 p-2 text-sm"
                        >
                          <p className="flex-1 text-gray-700 dark:text-gray-300 line-clamp-2 text-xs">
                            {sol.kb_solution_text}
                          </p>
                          <AppendButton
                            label={used ? t("knowledgeBase:appended") : t("knowledgeBase:appendToSolution")}
                            disabled={used}
                            onClick={() => appendOnce(key, setAiSolutionText, sol.kb_solution_text)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {kbIssueId && linkedSolutions.length === 0 && (
                  <p className="text-xs text-gray-400 italic">
                    {t("knowledgeBase:noLinkedSolutions")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ===== RIGHT: AI Text Fields ===== */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
              {t("knowledgeBase:aiFields")}
            </h3>

            <div>
              <Label htmlFor="ai_issue_text" value={t("knowledgeBase:aiIssueText")} />
              <Textarea
                id="ai_issue_text"
                rows={8}
                value={aiIssueText}
                onChange={(e) => setAiIssueText(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="ai_solution_text" value={t("knowledgeBase:aiSolutionText")} />
              <Textarea
                id="ai_solution_text"
                rows={8}
                value={aiSolutionText}
                onChange={(e) => setAiSolutionText(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button color="primary" onClick={handleSubmit}>
          {t("common:save")}
        </Button>
        <Button color="gray" onClick={onClose}>
          {t("common:cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function AppendButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1 text-xs ${disabled ? "text-gray-300 dark:text-gray-600 cursor-not-allowed" : "text-primary-600 dark:text-primary-400 hover:underline"}`}
    >
      <HiArrowRight className="h-3 w-3" />
      {label}
    </button>
  );
}

function SourceBlock({
  label,
  text,
  actionLabel,
  disabled,
  onAction,
  clamp,
}: {
  label: string;
  text: string;
  actionLabel: string;
  disabled: boolean;
  onAction: () => void;
  clamp?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-3">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <p className={`mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap ${clamp ? "line-clamp-6" : ""}`}>
        {text}
      </p>
      <div className="mt-2">
        <AppendButton label={actionLabel} disabled={disabled} onClick={onAction} />
      </div>
    </div>
  );
}
